/**
 * Utilitaires de tri pour les factures
 *
 * Fournit des fonctions de comparaison pour trier les factures
 * selon différents critères (numéro, montant, date, etc.)
 */

const STATUTS = ['Brouillon', 'A envoyer', 'Emise', 'Payée'];

/**
 * Comparateurs de tri par colonne
 * Chaque fonction retourne un comparateur (a, b) => number
 */
const SORT_COMPARATORS = {
  numero: (a, b) => {
    const aVal = a.fields?.['N° facture'] || '';
    const bVal = b.fields?.['N° facture'] || '';
    return aVal.localeCompare(bVal);
  },

  periode: (a, b, activeTab) => {
    let aVal, bVal;
    if (activeTab === 'externe') {
      aVal = (a.fields?.Année || 0) * 100 + (a.fields?.Mois || 0);
      bVal = (b.fields?.Année || 0) * 100 + (b.fields?.Mois || 0);
    } else {
      aVal = (a.fields?.Année || 0) * 10 + parseInt((a.fields?.Trimestre || 'Q0').replace('Q', ''));
      bVal = (b.fields?.Année || 0) * 10 + parseInt((b.fields?.Trimestre || 'Q0').replace('Q', ''));
    }
    return aVal - bVal;
  },

  tiers: (a, b, activeTab, maps) => {
    let aVal, bVal;
    if (activeTab === 'externe') {
      aVal = maps.prestataires[a.fields?.['Prestataire porteur']?.[0]]?.nom || '';
      bVal = maps.prestataires[b.fields?.['Prestataire porteur']?.[0]]?.nom || '';
    } else {
      aVal = maps.consultants[a.fields?.Consultant?.[0]]?.nom || '';
      bVal = maps.consultants[b.fields?.Consultant?.[0]]?.nom || '';
    }
    return aVal.localeCompare(bVal);
  },

  montant: (a, b) => {
    const aVal = a.fields?.['Montant total (HT)'] || 0;
    const bVal = b.fields?.['Montant total (HT)'] || 0;
    return aVal - bVal;
  },

  tauxtva: (a, b) => {
    const aVal = a.fields?.['Taux TVA (%)'] || 0;
    const bVal = b.fields?.['Taux TVA (%)'] || 0;
    return aVal - bVal;
  },

  tva: (a, b) => {
    const aVal = a.fields?.TVA || 0;
    const bVal = b.fields?.TVA || 0;
    return aVal - bVal;
  },

  montantTTC: (a, b) => {
    const aVal = a.fields?.['Montant total (TTC)'] || 0;
    const bVal = b.fields?.['Montant total (TTC)'] || 0;
    return aVal - bVal;
  },

  resteAPayer: (a, b) => {
    const aVal = a.fields?.['Reste à payer'] || 0;
    const bVal = b.fields?.['Reste à payer'] || 0;
    return aVal - bVal;
  },

  montantPaye: (a, b) => {
    const aVal = (a.fields?.['Montant total (TTC)'] || 0) - (a.fields?.['Reste à payer'] || 0);
    const bVal = (b.fields?.['Montant total (TTC)'] || 0) - (b.fields?.['Reste à payer'] || 0);
    return aVal - bVal;
  },

  dateEmission: (a, b) => {
    const aVal = a.fields?.['Date émission'] || '';
    const bVal = b.fields?.['Date émission'] || '';
    return aVal.localeCompare(bVal);
  },

  statut: (a, b) => {
    const aVal = STATUTS.indexOf(a.fields?.Statut || 'Brouillon');
    const bVal = STATUTS.indexOf(b.fields?.Statut || 'Brouillon');
    return aVal - bVal;
  },

  datePaiement: (a, b) => {
    const aVal = a.fields?.['Date paiement'] || '';
    const bVal = b.fields?.['Date paiement'] || '';
    return aVal.localeCompare(bVal);
  }
};

/**
 * Trie un tableau de factures selon une colonne et une direction
 *
 * @param {Array} factures - Tableau de factures à trier
 * @param {string} sortColumn - Nom de la colonne à trier
 * @param {'asc'|'desc'} sortDirection - Direction du tri
 * @param {'externe'|'interne'} activeTab - Type de facture (pour période et tiers)
 * @param {Object} maps - Maps prestataires et consultants
 * @param {Object} maps.prestataires - Map id -> prestataire
 * @param {Object} maps.consultants - Map id -> consultant
 * @returns {Array} Tableau trié
 */
export function sortFactures(factures, sortColumn, sortDirection, activeTab, maps) {
  if (!sortColumn || !SORT_COMPARATORS[sortColumn]) {
    return factures;
  }

  const comparator = SORT_COMPARATORS[sortColumn];

  const sorted = [...factures].sort((a, b) => {
    const comparison = comparator(a, b, activeTab, maps);
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  return sorted;
}

/**
 * Filtre les factures selon les critères sélectionnés
 *
 * @param {Array} factures - Tableau de factures à filtrer
 * @param {Object} filters - Objet contenant les filtres
 * @param {'externe'|'interne'} activeTab - Type de facture
 * @returns {Array} Tableau filtré
 */
export function filterFactures(factures, filters, activeTab) {
  let result = factures;

  // Filtre "Masquer les payées"
  if (filters.hidePaid) {
    result = result.filter(f => f.fields?.Statut !== 'Payée');
  }

  // Filtre année
  if (filters.annee) {
    result = result.filter(f => f.fields?.Année === filters.annee);
  }

  // Filtres spécifiques externe
  if (activeTab === 'externe') {
    if (filters.mois && filters.mois.length > 0) {
      result = result.filter(f => filters.mois.includes(f.fields?.Mois));
    }
    if (filters.prestataire && filters.prestataire.length > 0) {
      result = result.filter(f => filters.prestataire.includes(f.fields?.['Prestataire porteur']?.[0]));
    }
  }
  // Filtres spécifiques interne
  else {
    if (filters.trimestre) {
      result = result.filter(f => f.fields?.Trimestre === filters.trimestre);
    }
    if (filters.consultant) {
      result = result.filter(f => f.fields?.Consultant?.[0] === filters.consultant);
    }
  }

  // Filtre statut
  if (filters.statut && filters.statut.length > 0) {
    result = result.filter(f => filters.statut.includes(f.fields?.Statut || 'Brouillon'));
  }

  return result;
}

export { STATUTS };
